import { toPairs } from 'lodash-es'
import type { Container, ResolutionContext, ServiceIdentifier } from 'inversify'
import type { RegisteredServices } from '@/core/container/types'
import { SERVICE_TYPES } from '@/core/container/types'
import serviceContainer from '@/core/container/containerInstance'
import type { AppModule, Service, ServiceGroups, ServicesType } from '@/config/types'
import { getSymbolFor } from '@/utils/app'
import type { Dict, Factory } from '@/types'
import { isAppModuleDebug } from '@/utils/system'
import { moduleDebugger } from '@textmagic/vite-plugin-devtools-appmodule'
import { serviceToModuleMap } from '@/core/container/serviceModuleRegistry'
import { TmServiceNotBoundError } from '@/core/error/tmServiceNotBoundError'

const groupedServices = {} as Record<ServiceGroups, Service[]>

export const buildContainer = (
  services: ServicesType<string>,
  checkDeps?: (key: string, service: Service) => AppModule[],
): AppModule[] => {
  let result: AppModule[] = []
  for (const [key, service] of toPairs<Service>(services)) {
    if (!checkDeps) {
      bindService(key, service)
    } else {
      const modules = checkDeps(key, service)

      result = result.concat(modules)
      bindService(key, service)
    }
  }
  const uniqueModules = Array.from(new Set(result))

  // #region Related to vite-plugin-devtools-appmodule
  if (isAppModuleDebug) {
    for (const mod of uniqueModules) {
      moduleDebugger.emit({
        type: 'module:load:start',
        moduleName: mod,
        triggeredBy: 'buildContainer',
        time: Date.now(),
      })
    }
  }
  // #endregion

  return uniqueModules
}

export const dynamicClassDependsSymbol = Symbol('DynamicClassDependsSymbol')

export const bindService = (key: string, service: Service) => {
  if (!service.id) {
    service.id = key
  }
  const { bindingValue, bindingType } = service
  const onActivationHandler: <T>(context: ResolutionContext, injectable: T) => T | Promise<T> = (context, t) => {
    if (service.afterBindingHook) {
      service.afterBindingHook(key, service, t, context)
    }
    return t
  }
  const container = getContainer()
  if (bindingType === 'dynamicClass') {
    container
      .bind(getSymbolFor(key))
      .toDynamicValue((context: ResolutionContext) => {
        return bindingValue((name: RegisteredServices) => context.get(getSymbolFor(name)))
      })
      .inSingletonScope()
      .onActivation(onActivationHandler)
  } else if (!bindingType || bindingType === 'class') {
    container.bind(getSymbolFor(key)).to(bindingValue).inSingletonScope().onActivation(onActivationHandler)
  } else if (bindingType === 'factory') {
    container
      .bind<Factory<(factoryParam?: Dict) => unknown, [Dict | undefined]>>(getSymbolFor(key))
      .toFactory((context: ResolutionContext) => (factoryParam?: Dict) => {
        const createdService = context.get(bindingValue, { autobind: true })
        if (!service.afterServiceInstantiateHook) {
          return createdService
        }

        return service.afterServiceInstantiateHook(createdService, key, factoryParam ?? {})
      })
      .onActivation(onActivationHandler)
  } else {
    container.bind(getSymbolFor(key)).toConstantValue(bindingValue).onActivation(onActivationHandler)
  }
  if (service.group) {
    const serviceGroups = Array.isArray(service.group) ? service.group : [service.group]
    for (const serviceGroup of serviceGroups) {
      if (Array.isArray(groupedServices[serviceGroup])) {
        groupedServices[serviceGroup].push(service)
      } else {
        groupedServices[serviceGroup] = [service]
      }
    }
  }
  if (service.autoCreate) {
    get(key as RegisteredServices)
  }
}

export const getContainer = (): Container => serviceContainer

export type GetLocator = <T>(key: RegisteredServices | symbol) => T

export const get: GetLocator = <T>(key: RegisteredServices | symbol): T => {
  const symbol = getSymbolFor(key)

  if (!serviceContainer.isBound(symbol)) {
    const keyStr = typeof key === 'symbol' ? (key.description ?? '') : String(key)
    const module = serviceToModuleMap.get(keyStr)

    if (module) {
      throw new TmServiceNotBoundError(keyStr, module)
    }
  }

  return serviceContainer.get<T>(symbol)
}

getContainer().bind<GetLocator>('GetLocator').toConstantValue(get)

export const getFactory = <T>(key: RegisteredServices | symbol): Factory<T> =>
  getContainer().get<Factory<T>>(getSymbolFor(key))

export const getGroupedIds = (group: ServiceGroups, services?: Service[]): RegisteredServices[] => {
  const cb = (srv) => srv.id ?? srv.bindingValue
  if (Array.isArray(services)) {
    return services.filter((srv) => srv.group === group).map(cb)
  }
  return groupedServices[group]?.map(cb) ?? []
}

export const getGroupedInstances = <T>(group: ServiceGroups, services?: Service[]): Array<T> => {
  const grouped = getGroupedIds(group, services)
  return grouped?.map((id: RegisteredServices) => get<T>(id)) ?? []
}

export const getGroupedServices = (group: ServiceGroups, services?: Service[]): Service[] => {
  if (Array.isArray(services)) {
    return services.filter((srv) => srv.group === group)
  }
  return groupedServices[group] ?? []
}

export const unbind = <T>(service: symbol | ServiceIdentifier<T>) => {
  if (getContainer().isBound(service)) {
    getContainer().unbind(service)
  }
}

export const bindToConstantValue = <T>(service: symbol | ServiceIdentifier<T>, value: T) => {
  if (!getContainer().isBound(service)) {
    getContainer().bind(service).toConstantValue(value)
  }
}

export const isBound = (key: RegisteredServices | symbol): boolean => {
  return getContainer().isBound(getSymbolFor(key))
}

export { SERVICE_TYPES }
